import { useState, useEffect } from "react";
import { X, Link2, Tag, IndianRupee, ExternalLink, AlertCircle, CheckCircle } from "lucide-react";
import { createAffiliateLink } from "../../APIs/postApi";
import { trackEvent } from "../../utils/analytics";
import "./AddAffiliateLinkModal.css";

const NETWORKS = [
  { value: "none",     label: "Plain Link",  color: "#6B7280", hint: null },
  { value: "amazon",   label: "Amazon",      color: "#FF9900", hint: 'Find your tag in Amazon Associates dashboard (e.g. "picprofile-21")' },
  { value: "flipkart", label: "Flipkart",    color: "#2874F0", hint: "Get your affiliate ID from Flipkart Affiliate panel" },
  { value: "meesho",   label: "Meesho",      color: "#F43397", hint: "Use your Meesho referral code" },
  { value: "myntra",   label: "Myntra",      color: "#FF3F6C", hint: "Available in Myntra Partner program" },
  { value: "custom",   label: "Custom",      color: "#8B5CF6", hint: "Paste your pre-tagged URL — no code needed" },
];

const DEFAULT_LABELS = {
  none:     "Visit Link",
  amazon:   "Buy on Amazon",
  flipkart: "Buy on Flipkart",
  meesho:   "Shop on Meesho",
  myntra:   "Buy on Myntra",
  custom:   "Buy Now",
};

function isValidUrl(str) {
  try { new URL(str); return true; }
  catch { return false; }
}

function isAmazonTagValid(tag) {
  return /^[a-zA-Z0-9]+-\d+$/.test(tag.trim());
}

export default function AddAffiliateLinkModal({ isOpen, onClose, postId, onSuccess }) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  const [productName, setProductName] = useState("");
  const [url, setUrl] = useState("");
  const [network, setNetwork] = useState("none");
  const [affiliateTag, setAffiliateTag] = useState("");
  const [price, setPrice] = useState("");
  const [label, setLabel] = useState("");
  const [labelTouched, setLabelTouched] = useState(false);

  const [errors, setErrors] = useState({});
  const [amazonTagWarn, setAmazonTagWarn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', msg }

  // open/close animation (same pattern as BottomSheetModal)
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), 380);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // auto-fill label when network changes (unless creator already typed a custom label)
  useEffect(() => {
    if (!labelTouched) setLabel(DEFAULT_LABELS[network] || "Buy Now");
  }, [network, labelTouched]);

  function reset() {
    setProductName(""); setUrl(""); setNetwork("none");
    setAffiliateTag(""); setPrice(""); setLabel(""); setLabelTouched(false);
    setErrors({}); setAmazonTagWarn(false); setToast(null);
  }

  function handleClose() { reset(); onClose(); }

  function validate() {
    const e = {};
    if (productName.trim().length < 3) e.productName = "Product name must be at least 3 characters.";
    if (!url.trim())                    e.url = "URL is required.";
    else if (!isValidUrl(url.trim()))   e.url = "Enter a valid URL (include https://).";
    if (price && (isNaN(Number(price)) || Number(price) < 0)) e.price = "Price must be a positive number.";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    // Amazon tag soft-warning
    if (network === "amazon" && affiliateTag.trim() && !isAmazonTagValid(affiliateTag)) {
      setAmazonTagWarn(true);
    } else {
      setAmazonTagWarn(false);
    }

    // Empty tag → is_affiliate false (graceful: creator hasn't signed up yet)
    const hasTag = affiliateTag.trim() !== "";
    const tagRequiresNetwork = ["amazon", "flipkart", "meesho", "myntra"].includes(network);
    const isAffiliate = network !== "none" && (hasTag || network === "custom");

    const payload = {
      label:             label.trim() || DEFAULT_LABELS[network],
      url:               url.trim(),
      is_affiliate:      isAffiliate,
      affiliate_network: network,
      affiliate_tag:     affiliateTag.trim(),
      product_name:      productName.trim(),
      product_price:     price.trim() ? parseFloat(price).toFixed(2) : null,
      currency:          "INR",
    };

    // Suppress tag if it's for a tag-required network but tag is empty (user intent = save for later)
    if (tagRequiresNetwork && !hasTag) {
      payload.affiliate_tag = "";
    }

    setLoading(true);
    try {
      await createAffiliateLink(postId, payload);

      trackEvent("affiliate_link_added", {
        postId,
        metadata: { network, has_price: !!price.trim() },
      });

      setToast({ type: "success", msg: "Affiliate link added!" });
      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.url?.[0] ||
        Object.values(err?.response?.data || {})[0]?.[0] ||
        "Something went wrong. Please try again.";
      setToast({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  }

  if (!visible) return null;

  const selectedNetwork = NETWORKS.find((n) => n.value === network);
  const showTagField = network !== "none";

  const sheetStyle = {
    transform: animating ? "translateY(0)" : "translateY(100%)",
    transition: "transform 380ms cubic-bezier(0.32, 0.72, 0, 1)",
  };
  const desktopStyle = {
    transform: animating ? "scale(1)" : "scale(0.95)",
    opacity: animating ? 1 : 0,
    transition: "transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 200ms ease",
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center"
        onClick={handleClose}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            backgroundColor: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            opacity: animating ? 1 : 0,
          }}
        />

        {/* Sheet */}
        <div
          className="affiliate-modal-sheet relative w-full md:max-w-lg md:mx-4"
          style={window.innerWidth >= 768 ? desktopStyle : sheetStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="rounded-t-3xl md:rounded-3xl overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #0d1117 0%, #0a0e15 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 -8px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
            }}
          >
            {/* Drag handle (mobile) */}
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)" }}
                >
                  <Link2 className="w-4 h-4" style={{ color: "#818CF8" }} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Add Affiliate Link</p>
                  <p className="text-white/35 text-xs">Earn commission from product clicks</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full flex items-center justify-center
                  bg-white/5 hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/50" />
              </button>
            </div>

            {/* Toast */}
            {toast && (
              <div
                className="mx-6 mt-4 flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm"
                style={{
                  background: toast.type === "success"
                    ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
                  border: `1px solid ${toast.type === "success" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                  color: toast.type === "success" ? "#34D399" : "#F87171",
                }}
              >
                {toast.type === "success"
                  ? <CheckCircle className="w-4 h-4 shrink-0" />
                  : <AlertCircle className="w-4 h-4 shrink-0" />}
                {toast.msg}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 pt-5 pb-6 space-y-4">

              {/* Product Name */}
              <div>
                <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                  Product Name <span className="text-indigo-400">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Sony WH-1000XM5 Headphones"
                  maxLength={200}
                  value={productName}
                  onChange={(e) => { setProductName(e.target.value); setErrors((p) => ({ ...p, productName: undefined })); }}
                  className="affiliate-input w-full"
                />
                {errors.productName && <p className="text-red-400 text-xs mt-1">{errors.productName}</p>}
              </div>

              {/* Product URL */}
              <div>
                <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                  Product URL <span className="text-indigo-400">*</span>
                </label>
                <div className="relative">
                  <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                  <input
                    type="url"
                    placeholder="https://amazon.in/dp/B09XYZ"
                    value={url}
                    onChange={(e) => { setUrl(e.target.value); setErrors((p) => ({ ...p, url: undefined })); }}
                    className="affiliate-input w-full pl-9"
                  />
                </div>
                {errors.url && <p className="text-red-400 text-xs mt-1">{errors.url}</p>}
              </div>

              {/* Network + Price row */}
              <div className="flex gap-3">
                {/* Network */}
                <div className="flex-1">
                  <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                    Network
                  </label>
                  <div className="relative">
                    {selectedNetwork && (
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
                        style={{ background: selectedNetwork.color }}
                      />
                    )}
                    <select
                      value={network}
                      onChange={(e) => { setNetwork(e.target.value); setAmazonTagWarn(false); }}
                      className="affiliate-input w-full pl-7 appearance-none"
                    >
                      {NETWORKS.map((n) => (
                        <option key={n.value} value={n.value}>{n.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Price */}
                <div className="w-32">
                  <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                    Price
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={price}
                      onChange={(e) => { setPrice(e.target.value); setErrors((p) => ({ ...p, price: undefined })); }}
                      className="affiliate-input w-full pl-8"
                    />
                  </div>
                  {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
                </div>
              </div>

              {/* Affiliate Tag (conditional) */}
              {showTagField && (
                <div>
                  <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                    <Tag className="inline w-3 h-3 mr-1 -mt-0.5" />
                    Affiliate Tag
                    <span className="ml-1 text-white/25 normal-case font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder={network === "amazon" ? "picprofile-21" : "your-affiliate-id"}
                    value={affiliateTag}
                    onChange={(e) => { setAffiliateTag(e.target.value); setAmazonTagWarn(false); }}
                    className="affiliate-input w-full"
                  />
                  {selectedNetwork?.hint && (
                    <p className="text-white/30 text-xs mt-1.5 leading-relaxed">
                      {selectedNetwork.hint}
                    </p>
                  )}
                  {amazonTagWarn && (
                    <p className="text-amber-400 text-xs mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      Amazon tags usually end with -21 (e.g. picprofile-21). Check your Associates account.
                    </p>
                  )}
                  {network !== "none" && !affiliateTag.trim() && (
                    <p className="text-white/25 text-xs mt-1.5">
                      No tag? Link will be saved as a plain link — add your tag later when you sign up.
                    </p>
                  )}
                </div>
              )}

              {/* Button Label */}
              <div>
                <label className="block text-white/50 text-xs font-medium mb-1.5 uppercase tracking-wider">
                  Button Label
                </label>
                <input
                  type="text"
                  placeholder={DEFAULT_LABELS[network]}
                  maxLength={100}
                  value={label}
                  onChange={(e) => { setLabel(e.target.value); setLabelTouched(true); }}
                  className="affiliate-input w-full"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 rounded-2xl text-sm font-medium text-white/50
                    hover:text-white/70 transition-colors"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 rounded-2xl text-sm font-semibold
                    transition-all duration-200 disabled:opacity-50"
                  style={{
                    background: loading
                      ? "rgba(99,102,241,0.4)"
                      : "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                    color: "#fff",
                    boxShadow: loading ? "none" : "0 4px 20px rgba(99,102,241,0.35)",
                  }}
                >
                  {loading ? "Saving…" : "Add Link"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
